import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import SEO from '../components/common/SEO';
import Layout from '../components/layout/Layout';
import Section from '../components/common/Section';
import { siteConfig } from '../config/site';

interface Post {
  id: number;
  slug: string;
  title: string;
  excerpt: string | null;
  body: string;
  cover_image_url: string | null;
  created_at: string;
  updated_at: string;
}

const Wrapper = styled.article`
  max-width: 800px;
  margin: 0 auto;
  color: var(--text-color);

  h1 { font-size: 2.25rem; margin-bottom: 0.5rem; line-height: 1.2; }
  .meta { color: var(--text-secondary); font-size: 0.9rem; margin-bottom: 2rem; }
  .cover { width: 100%; border-radius: var(--border-radius); margin-bottom: 2rem; }
  .body { line-height: 1.75; font-size: 1.05rem; }
  .body img { max-width: 100%; border-radius: var(--border-radius); margin: 1.5rem 0; }
  .body h2 { margin-top: 2.5rem; font-size: 1.6rem; }
  .body h3 { margin-top: 1.75rem; font-size: 1.25rem; }
  .body p { margin-bottom: 1rem; }
  .body ul, .body ol { margin: 1rem 0 1rem 1.5rem; }
  .body li { margin-bottom: 0.5rem; }
  .body a { color: var(--primary-color); text-decoration: underline; }
  .body pre {
    background: rgba(0,0,0,0.35);
    padding: 1rem;
    border-radius: var(--border-radius);
    overflow-x: auto;
  }
  .body code {
    background: rgba(0,0,0,0.25);
    padding: 0.1rem 0.3rem;
    border-radius: 4px;
  }
`;

const YouTubeWrap = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  margin: 1.75rem 0;
  border-radius: var(--border-radius);
  background: #000;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const Notice = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary);
`;

function formatDateISO(iso: string): string {
  try {
    return new Date(iso.replace(' ', 'T') + 'Z').toISOString();
  } catch {
    return iso;
  }
}

function formatDateHuman(iso: string): string {
  try {
    return new Date(iso.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function readingTime(text: string): number {
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

interface Segment {
  kind: 'md' | 'youtube';
  content: string;
}

function segmentBody(body: string): Segment[] {
  const parts = body.split(/\{\{youtube:([A-Za-z0-9_-]{11})\}\}/g);
  const out: Segment[] = [];
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      if (parts[i]) out.push({ kind: 'md', content: parts[i] });
    } else {
      out.push({ kind: 'youtube', content: parts[i] });
    }
  }
  return out;
}

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'notfound' | 'error'>('loading');

  useEffect(() => {
    if (!slug) return;
    setState('loading');
    fetch(`/api/posts/${encodeURIComponent(slug)}`)
      .then(async (r) => {
        if (r.status === 404) {
          setState('notfound');
          return;
        }
        if (!r.ok) throw new Error('load');
        const data = await r.json();
        setPost(data.post);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [slug]);

  const segments = useMemo(() => (post ? segmentBody(post.body) : []), [post]);
  const canonical = post ? `${siteConfig.domain}/blog/${post.slug}` : undefined;

  useEffect(() => {
    if (!post) return;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt || '',
      image: post.cover_image_url
        ? [post.cover_image_url.startsWith('http') ? post.cover_image_url : `${siteConfig.domain}${post.cover_image_url}`]
        : undefined,
      datePublished: formatDateISO(post.created_at),
      dateModified: formatDateISO(post.updated_at),
      author: { '@type': 'Organization', name: siteConfig.brand.name, url: siteConfig.domain },
      publisher: { '@type': 'Organization', name: siteConfig.brand.name, url: siteConfig.domain },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    };
    const scriptId = 'blog-jsonld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);
    return () => {
      script?.remove();
    };
  }, [post, canonical]);

  return (
    <Layout>
      <SEO
        title={post ? `${post.title} | ${siteConfig.brand.name}` : `Blog | ${siteConfig.brand.name}`}
        description={post?.excerpt || `Read the latest article on ${siteConfig.brand.name}.`}
        canonicalUrl={canonical}
      />
      <Section background="light" padding="large">
        {state === 'loading' && <Notice>Loading…</Notice>}
        {state === 'notfound' && <Notice>Post not found.</Notice>}
        {state === 'error' && <Notice>Could not load this post.</Notice>}
        {state === 'ready' && post && (
          <Wrapper>
            <h1>{post.title}</h1>
            <div className="meta">
              Published {formatDateHuman(post.created_at)} · {readingTime(post.body)} min read
            </div>
            {post.cover_image_url && (
              <img className="cover" src={post.cover_image_url} alt={post.title} />
            )}
            <div className="body">
              {segments.map((seg, i) =>
                seg.kind === 'md' ? (
                  <ReactMarkdown key={i}>{seg.content}</ReactMarkdown>
                ) : (
                  <YouTubeWrap key={i}>
                    <iframe
                      src={`https://www.youtube.com/embed/${seg.content}`}
                      title="YouTube video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                  </YouTubeWrap>
                ),
              )}
            </div>
          </Wrapper>
        )}
      </Section>
    </Layout>
  );
};

export default BlogPostPage;
